import { Dispatch, SetStateAction, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@weirgroup/weir-dls'

import { ReplacementPartType } from '../../types/bucketTypes'
import { SingleAssetGeofencesType } from '../../types/geofence.type'
import { ShoppingListItem } from '../../types/shopping-list.type'

import { ConfirmDialog } from '../confirm-dialog/confirm-dialog'

import geofenceApi from '../../api/geofence.api'
import { addToShoppingList } from '../../api/shopping-list.api'

import './styles.scss'
import { ToastDataType } from '../../types/toast.type'
import { GEOFENCE_SHAPE } from '../../constants/bucket.contants'
import { useDateConverter } from '../../hooks/use-date-converter'
import { useGlobalError } from '../../contexts/global-error.context'
import { getErrorMessage } from '../../utils/api-error-handler'
import { useAnalyticsEvent } from '../../hooks/useAnalyticsEvent'

interface Props {
    type: string
    cardInfo?: ReplacementPartType
    nonEsco?: boolean
    geofenceData?: SingleAssetGeofencesType
    shoppingListData?: ShoppingListItem
    editHandler?: any
    setLoader?: Dispatch<SetStateAction<boolean>>
    renderToastMessage?: (toastData: ToastDataType) => void
    setIsAssetUpdated?: Dispatch<SetStateAction<boolean>>
    isGuest?: boolean,
    darkMode?: boolean
    onPartDeleted?: (deletedPartId: number) => void
    onShoppingListDelete?: (itemId: number) => void
    onShoppingListEdit?: (item: ShoppingListItem) => void
}

export const Card = (props: Props) => {
    const {
    type,
    cardInfo,
    nonEsco,
    geofenceData,
    shoppingListData,
    setLoader,
    renderToastMessage,
    editHandler,
    setIsAssetUpdated,
    isGuest,
    darkMode,
    onShoppingListDelete,
    onShoppingListEdit,
    } = props
    const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false)
    const logAnalyticsEvent = useAnalyticsEvent();
    const { t } = useTranslation()
    const { convertDateStringToFormat } = useDateConverter()
    const {showError,showSuccess} = useGlobalError()
    const addItemToShoppingList = () => {
        if (setLoader) {
            setLoader(true)
            if (cardInfo) {
                addToShoppingList(cardInfo.part_id)
                    .then(() => {
                        showSuccess(t('AddToListSuccess'))
                        logAnalyticsEvent('web_user_add_part_to_shopping_list', { description: 'User added part to shopping list', partId: cardInfo.part_id });
                        setLoader(false)
                    })
                    .catch((err: any) => {
                        showError(getErrorMessage(err))
                        setLoader(false)
                    })
            }
        }
    }

    const triggerToastMessage = (success: boolean, result?: any) => {
        if (renderToastMessage) {
            const toastData: ToastDataType = {
                message: success ? result.data : result.response.data.message,
                variant: success ? 'success' : 'critical',
            }
            renderToastMessage(toastData)
        }
    }

    const deleteGeofence = () => {
        if (geofenceData && setLoader) {
            setLoader(true)
            geofenceApi
                .deleteGeofence(geofenceData.geofence_id)
                .then((result: any) => {
                    if (setIsAssetUpdated) setIsAssetUpdated(true)
                    triggerToastMessage(true, result)
                    setLoader(false)
                    logAnalyticsEvent('web_user_deleted_a_geofence', { description: 'User deleted a geofence' });
                })
                .catch((err: any) => {
                    console.error(err)
                    if (setIsAssetUpdated) setIsAssetUpdated(true)
                    triggerToastMessage(false, err)
                    setLoader(false)
                })
        }
    }

    const renderActionButtons = (isReplacementParts?: boolean) => {
        return (
            <>
                <div
                    className={
                        isGuest ? 'isGuest btn-container' : 'btn-container'
                    }
                    onClick={() =>
                        isReplacementParts
                            ? editHandler(true, cardInfo)
                            : editHandler(geofenceData)
                    }
                    role="button"
                >
                    <Icon name={'Edit'} darkMode={darkMode}/>
                </div>
                <div
                    className={
                        isGuest ? 'isGuest btn-container' : 'btn-container'
                    }
                    onClick={() => setIsConfirmOpen(true)}
                    role="button"
                >
                    <Icon name={'Trash'} darkMode={darkMode} />
                </div>
            </>
        )
    }

    const replaceablePartCard = () => {
        return (
            <div className="card-container">
                {isConfirmOpen && cardInfo ? (
                    <ConfirmDialog
                        darkMode={darkMode}
                        isConfirmOpen={isConfirmOpen}
                        cancelHandler={() => setIsConfirmOpen(false)}
                        confirmHandler={() => {
                            props?.onPartDeleted?.(cardInfo.part_id);
                            logAnalyticsEvent('web_user_deleted_part_in_bill_of_material', { description: 'User deleted part in bill of material', assetId: cardInfo.part_id });
                            setIsConfirmOpen(false);
                        }}
                    />
                ) : (
                    <></>
                )}
                <div
                    className={
                        nonEsco
                            ? 'color-status gray-status-bg'
                            : 'color-status red-status-bg'
                    }
                ></div>
                <div className="details">
                    <div className="header">
                        <div className="title">
                            <p>{cardInfo?.description}</p>
                        </div>
                        <div className="sub-details">
                            <p>
                                {t('quantity')}:{' '}
                                <span>{cardInfo?.quantity}</span>
                            </p>
                        </div>
                    </div>
                    <div className="body">
                        <div className="info">
                            <p className="category">
                                {t('partNumber')}:{' '}
                                <span>{cardInfo?.part_number}</span>
                            </p>
                            <p className="category">
                                {t('pmid')}: <span>{cardInfo?.pmid}</span>
                            </p>
                            {cardInfo?.notes ? (
                                <p className="category">
                                    {t('notes')}: <span>{cardInfo?.notes}</span>
                                </p>
                            ) : (
                                <></>
                            )}
                        </div>
                        <div
                            className={
                                nonEsco
                                    ? 'icons non-esco-flex'
                                    : 'icons'
                            }
                        >
                            {nonEsco ? (
                                <>{renderActionButtons(true)}</>
                            ) : (
                                <div
                                    className={
                                        isGuest
                                            ? 'isGuest btn-container'
                                            : 'btn-container'
                                    }
                                    onClick={() => addItemToShoppingList()}
                                    role="button"
                                >
                                    <Icon name={'DXPPlus'} darkMode={darkMode} />{' '}
                                    {t('addToShoppingList')}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const geofenceCard = (geofenceData: SingleAssetGeofencesType) => {
        return (
            <div className="card-container">
                {isConfirmOpen ? (
                    <ConfirmDialog
                        isConfirmOpen={isConfirmOpen}
                        confirmHandler={() => deleteGeofence()}
                        cancelHandler={() => setIsConfirmOpen(false)}
                    />
                ) : (
                    <></>
                )}
                <div className="color-status red-status-bg"></div>
                <div className="details">
                    <div className="header">
                        <div className="title">
                            <p>
                                {t('geofence')}: {geofenceData?.name}
                            </p>
                        </div>
                        <div className="sub-details font-color-black">
                            <p>
                                {t('updated')}:{' '}
                                <span>
                                    {convertDateStringToFormat(geofenceData.updated_at)}
                                </span>
                            </p>
                        </div>
                    </div>
                    <div className="body geofence-body">
                        <div className="geofence-flex font-color-gray">
                            {geofenceData?.shape == GEOFENCE_SHAPE.CIRCLE ? (
                                <p className="category ">
                                    {t('radius')}:{' '}
                                    <span>{geofenceData?.radius}</span>
                                </p>
                            ) : (
                                <></>
                            )}
                            <p className="category">
                                {t('created')}:{' '}
                                <span>
                                    {geofenceData
                                        ? convertDateStringToFormat(geofenceData.created_at)
                                        : ''}
                                </span>
                            </p>
                            <p className="category">
                                {t('shape')}: <span>{geofenceData?.shape}</span>
                            </p>
                        </div>
                        <div className="icons add-to-shopping-list-flex">
                            {renderActionButtons()}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const shoppingListCard = (item: ShoppingListItem) => {
        return (
            <div className="card-container">
                <div className="color-status red-status-bg"></div>
                <div className="details">
                    <div className="header">
                        <div className="title">
                            <p>{item.part.description}</p>
                        </div>
                        <div className="sub-details">
                            <p>
                                {t('quantity')}: <span>{item.quantity}</span>
                            </p>
                        </div>
                    </div>
                    <div className="body shopping-list-body-layout">
                        <div className="info">
                            <p className="category">
                                {t('partNumber')}: <span>{item.part.part_number}</span>
                            </p>
                            <p className="category">
                                {t('pmid')}: <span>{item.part.pmid}</span>
                            </p>
                        </div>
                        <div className="icons non-esco-flex">
                            <button
                                type="button"
                                className={
                                    isGuest
                                        ? 'isGuest btn-container'
                                        : 'btn-container'
                                }
                                onClick={() => onShoppingListEdit?.(item)}
                                disabled={isGuest}
                            >
                                <Icon name={'Edit'} darkMode={darkMode} />
                            </button>
                            <button
                                type="button"
                                className={
                                    isGuest
                                        ? 'isGuest btn-container'
                                        : 'btn-container'
                                }
                                onClick={() =>
                                    onShoppingListDelete?.(item.shopping_cart_item_id)
                                }
                                disabled={isGuest}
                            >
                                <Icon name={'Trash'} darkMode={darkMode} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (type === 'geofence' && geofenceData) {
        return geofenceCard(geofenceData)
    }

    if (type === 'shopping-list' && shoppingListData) {
        return shoppingListCard(shoppingListData)
    }

    return replaceablePartCard()
}
